
import { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, DollarSign, Receipt, Wallet, BarChart3, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import { reportsApi } from "@/lib/api";
import { formatCurrencyFull, localToday } from "@/lib/data";

const fmt = (n: number) => formatCurrencyFull(n);

function StatCard({ label, value, sub, icon: Icon, color, bg }: any) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-stone-100`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg ${color} bg-white/80 flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-xs font-medium text-stone-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-stone-800">{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function FinancialSummaryPage() {
  const today = localToday();
  const firstOfMonth = today.slice(0, 7) + "-01";

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsApi.financialSummary({ from_date: fromDate, to_date: toDate });
      setData(res.data?.data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [fromDate, toDate]);

  const revenue       = data?.revenue?.total ?? 0;
  const expenses      = data?.expenses?.total ?? 0;
  const vendorPaid    = data?.expenses?.vendor_payments?.total ?? 0;
  const operational   = data?.expenses?.operational?.total ?? 0;
  const profit        = data?.profit_loss?.gross_profit ?? 0;
  const margin        = data?.profit_loss?.profit_margin ?? 0;
  const isProfit      = profit >= 0;

  const byCategory: any[] = data?.expenses?.by_category ?? [];
  const byMethod: Record<string, any[]> = data?.revenue?.by_payment_method ?? {};

  const methodTotals = Object.entries(byMethod).map(([method, rows]) => ({
    method,
    total: (rows as any[]).reduce((s, r) => s + Number(r.total), 0),
    count: (rows as any[]).reduce((s, r) => s + Number(r.count), 0),
  }));

  const expenseChartData = byCategory.map(c => ({
    name: c.name.length > 16 ? c.name.slice(0, 15) + "…" : c.name,
    amount: Number(c.total),
  }));

  const plData = [
    { name: "Revenue", amount: revenue, fill: "#10b981" },
    { name: "Expenses", amount: expenses, fill: "#f59e0b" },
    { name: isProfit ? "Profit" : "Loss", amount: Math.abs(profit), fill: isProfit ? "#3b82f6" : "#ef4444" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            <h1 className="text-lg font-bold text-stone-800">Financial Summary</h1>
          </div>
          <DateRangePicker
            fromDate={fromDate} toDate={toDate}
            onFromChange={setFromDate} onToChange={setToDate}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-stone-400 text-sm">Loading…</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Total Revenue" value={fmt(revenue)} sub={`${data?.revenue?.transactions ?? 0} payments`} icon={DollarSign} color="text-emerald-600" bg="bg-emerald-50" />
              <StatCard label="Total Expenses" value={fmt(expenses)} sub={`Vendor: ${fmt(vendorPaid)}`} icon={Receipt} color="text-amber-600" bg="bg-amber-50" />
              <StatCard label={isProfit ? "Net Profit" : "Net Loss"} value={fmt(Math.abs(profit))} sub={`${Math.abs(margin).toFixed(1)}% margin`} icon={isProfit ? TrendingUp : TrendingDown} color={isProfit ? "text-blue-600" : "text-red-600"} bg={isProfit ? "bg-blue-50" : "bg-red-50"} />
              <StatCard label="Vendor Payments" value={fmt(vendorPaid)} sub={`Operational: ${fmt(operational)}`} icon={Wallet} color="text-violet-600" bg="bg-violet-50" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* P&L Chart */}
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <h2 className="text-sm font-semibold text-stone-700 mb-4">Revenue vs Expenses vs Profit</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={plData} barCategoryGap="40%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: any) => fmt(v)} />
                    <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                      {plData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Expense by Category */}
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <h2 className="text-sm font-semibold text-stone-700 mb-4">Expenses by Category</h2>
                {expenseChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-52 text-stone-400 text-sm">No expense data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={expenseChartData} layout="vertical" barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                      <Tooltip formatter={(v: any) => fmt(v)} />
                      <Bar dataKey="amount" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Payment Method Breakdown */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <h2 className="text-sm font-semibold text-stone-700 mb-3">Revenue by Payment Method</h2>
              {methodTotals.length === 0 ? (
                <p className="text-sm text-stone-400">No payment data for this period</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {methodTotals.map(m => (
                    <div key={m.method} className="bg-stone-50 rounded-lg p-3 border border-stone-100">
                      <p className="text-xs font-medium text-stone-500 truncate">{m.method || "Unknown"}</p>
                      <p className="text-base font-bold text-stone-800 mt-1">{fmt(m.total)}</p>
                      <p className="text-xs text-stone-400">{m.count} payment{m.count !== 1 ? "s" : ""}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary Table */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <h2 className="text-sm font-semibold text-stone-700 mb-3">P&L Summary</h2>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-stone-100">
                  <tr className="py-2">
                    <td className="py-2.5 text-stone-600">Total Revenue (Collected)</td>
                    <td className="py-2.5 text-right font-semibold text-emerald-700">{fmt(revenue)}</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 text-stone-600 pl-4 text-xs">Vendor Payments</td>
                    <td className="py-2.5 text-right text-stone-500">– {fmt(vendorPaid)}</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 text-stone-600 pl-4 text-xs">Operational Expenses</td>
                    <td className="py-2.5 text-right text-stone-500">– {fmt(operational)}</td>
                  </tr>
                  <tr className="bg-stone-50">
                    <td className="py-2.5 font-semibold text-stone-800">Total Expenses</td>
                    <td className="py-2.5 text-right font-semibold text-amber-700">– {fmt(expenses)}</td>
                  </tr>
                  <tr className={isProfit ? "bg-emerald-50" : "bg-red-50"}>
                    <td className={`py-3 font-bold text-base ${isProfit ? "text-emerald-800" : "text-red-800"}`}>
                      {isProfit ? "Net Profit" : "Net Loss"}
                    </td>
                    <td className={`py-3 text-right font-bold text-base ${isProfit ? "text-emerald-700" : "text-red-700"}`}>
                      {isProfit ? "" : "– "}{fmt(Math.abs(profit))}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2.5 text-stone-500 text-xs">Profit Margin</td>
                    <td className={`py-2.5 text-right font-medium text-xs ${isProfit ? "text-emerald-600" : "text-red-600"}`}>
                      {margin.toFixed(2)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
